/**
 * VehicleDetailsModalPremium - Modal 360º do veículo
 *
 * Segue a mesma arquitetura visual do modal premium de cliente
 * (header 48px + abas 36px + sidebar de ficha + conteúdo), reaproveitando
 * componentes já existentes (CustomerChatTab, VehicleFinesDialog,
 * VehicleFormDialog).
 *
 * Abas: Resumo, Andamentos, Negócios, Contratos, Tarefas, Chat e Multas.
 *
 * @version 2.11.0
 * RELEASE: 2026-10-06 v2.11.0 - Prop opcional `initialTab`: quando informada, a aba
 *          inicial é aplicada a cada abertura (usado pelo Workflow para abrir na
 *          aba "Fluxos"). Sem a prop, comportamento anterior preservado.
 * RELEASE: 2026-10-02 v2.10.0 - Tempo real por registro: useVehicleRecordRealtime (status,
 *          dados e lista atualizam sem F5) e useWorkflowRecordRealtime (aba Fluxos).
 * RELEASE: 2026-10-02 v2.9.0 - Aba "Fluxos" (Workflow com raiz Veículo), reaproveitando
 *          o WorkflowProcessPanel; visível só com o módulo Workflow ligado.
 * RELEASE: 2026-09-29 v2.8.1 - Foto de perfil do veículo movida do header para a sidebar
 *   direita (seção Identificação, primeira opção); permissão canEditPhoto repassada.
 * RELEASE: 2026-08-20 v2.8.0 - Responsividade mobile (padrão DealDetailsModalPremium):
 *   modal em tela cheia no celular e sidebar injetada no topo da aba Resumo.
 * RELEASE: 2026-08-09 v2.5.0 - Permissões granulares nas abas: "Negócios" só aparece com
 *   permissão de visualizar negócios (crm_deals) e "Tarefas" exige módulo tasks_enabled +
 *   permissão tasks_list. Botão "Novo Negócio" oculto quando não pode visualizar negócios.
 * RELEASE: 2026-08-09 v2.4.0 - Clique em um negócio na aba "Negócios" abre o modal 360º
 *   do negócio (DealDetailsModalPremium), mantendo o modal do veículo aberto
 * RELEASE: 2026-08-08 v2.1.0 - Abas Negócios/Contratos/Tarefas passam a usar os componentes
 *   premium do modal de cliente (CustomerDealsTab, CustomerContractsTab, TasksRelatedList)
 *   filtrando por veículo
 * RELEASE: 2026-08-08 v2.0.0 - Padronização 100% com o modal premium de cliente
 *   - Sidebar movida para a DIREITA (360px) com cards flutuantes
 *   - Aba Resumo com cards de métrica + andamentos recentes (VehicleSummaryTab)
 *   - Wrapper de conteúdo com scroll próprio (p-2 md:p-4) e TooltipProvider
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useVehicleEquipment } from '@/hooks/useStockMovements';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useServiceOrdersList } from '@/hooks/useServiceOrders';
import { ServiceOrdersEmbeddedList } from '@/components/service-orders/ServiceOrdersEmbeddedList';
import { useEffect, useState } from 'react';
import { ReceiptText } from 'lucide-react';
// v2.8.0: layout em tela cheia no mobile
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useVehicleDetails } from '@/hooks/useVehicleDetails';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
// v2.5.0: flag de módulo de tarefas do tenant (tasks_enabled)
import { useAuth } from '@/hooks/useAuth';
import { VehicleModalHeader } from './VehicleModalHeader';
import { VehicleModalSidebar } from './VehicleModalSidebar';
import { VehicleFinanceiroTab } from './VehicleFinanceiroTab';
import { useVehicleFinanceiro } from '@/hooks/useFinanceiroVehicles';
import { VehicleModalTabs, type VehicleModalTab } from './VehicleModalTabs';
import { VehicleAttachmentsTab } from './VehicleAttachmentsTab';
import { VehicleHistoryTab } from './VehicleHistoryTab';
import { VehicleInspectionsTab } from './VehicleInspectionsTab';
import { VehicleEquipmentTab } from './VehicleEquipmentTab';
import { useStockModuleEnabled } from '@/hooks/useStockModule';
import { useChecklistModuleEnabled } from '@/hooks/useChecklistModule';
// v2.9.0: processos do Workflow com raiz Veículo
import { WorkflowProcessPanel } from '@/components/workflow/WorkflowProcessPanel';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';
// v2.10.0: tempo real por registro (veículo e processos do Workflow)
import { useVehicleRecordRealtime } from '@/hooks/useVehicleRecordRealtime';
import { useWorkflowRecordRealtime } from '@/hooks/useWorkflowRecordRealtime';
// v2.7.0: aba de transferências (ledger de posse) + modal de transferência
import { VehicleTransfersTab } from './VehicleTransfersTab';
import { VehicleTransferDialog } from '@/components/vehicles/VehicleTransferDialog';
import { useVehicleOwnershipHistory } from '@/hooks/useVehicleOwnership';
import { VehicleSummaryTab } from './VehicleSummaryTab';
import { CustomerDealsTab } from '@/components/crm/customers/modal/tabs/CustomerDealsTab';
import { CustomerContractsTab } from '@/components/crm/customers/modal/tabs/CustomerContractsTab';
// v: subabas por tipo de tarefa (Normal | Retenção | Jurídico)
import { TasksRelatedTabs } from '@/components/tasks/related/TasksRelatedTabs';
import { VehicleFinesDialog } from '@/components/vehicles/VehicleFinesDialog';
// v2.9.0: formulário de edição aberto pelo próprio modal (fallback sem onEdit)
import { VehicleFormDialog } from '@/components/vehicles/VehicleFormDialog';
import type { CustomerVehicle } from '@/hooks/useCustomerVehicles';
import { CustomerChatTab } from '@/components/crm/customers/modal/tabs/CustomerChatTab';
// v2.6.0: modal de conversa aberto a partir da aba "Chat"
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
// v2.4.0: modal 360º do negócio aberto a partir da aba "Negócios"
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';



interface VehicleDetailsModalPremiumProps {
  vehicleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Abre o formulário de edição do veículo */
  onEdit?: (vehicleId: string) => void;
  /** v2.11.0: aba inicial aplicada a cada abertura (ex.: aba "workflow" via Workflow) */
  initialTab?: VehicleModalTab;
}

/** Formata valores monetários em BRL */
const formatCurrency = (value: number | null) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Modal de detalhes do veículo */
export function VehicleDetailsModalPremium({
  vehicleId,
  open,
  onOpenChange,
  onEdit,
  // v2.11.0: aba inicial opcional (ex.: "workflow" quando aberto pelo Workflow)
  initialTab,
}: VehicleDetailsModalPremiumProps) {
  const [activeTab, setActiveTab] = useState<VehicleModalTab>('summary');

  // v2.11.0: aplica a aba inicial a cada abertura do modal (sem prop, não interfere)
  useEffect(() => {
    if (open && initialTab) setActiveTab(initialTab);
  }, [open, initialTab]);

  const [finesOpen, setFinesOpen] = useState(false);
  /**
   * v2.9.0: fallback de edição — quando o componente pai não fornece `onEdit`,
   * o próprio modal abre o VehicleFormDialog (antes o botão não fazia nada).
   */
  const [editOpen, setEditOpen] = useState(false);
  /** v2.7.0: modal de transferência de propriedade */
  const [transferOpen, setTransferOpen] = useState(false);
  /** Contador exibido no badge da aba "Anexos" */
  const [attachmentsCount, setAttachmentsCount] = useState<number | undefined>(undefined);
  /** v2.4.0: negócio selecionado na aba "Negócios" para abrir o modal 360º */
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  /** v2.6.0: conversa selecionada na aba "Chat" (abre ChatConversationModal) */
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  /** Abre a conversa sem fechar o modal do veículo */
  const handleOpenConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsChatModalOpen(true);
  };


  /** Abre o modal de detalhes do negócio sem fechar o modal do veículo */
  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };

  // v2.8.0: no mobile o modal ocupa a tela toda e a ficha vai para o Resumo
  const isMobile = useIsMobile();

  const { finesEnabled } = useVehiclesModule();
  const { currentTenant } = useAuth();
  const { canView, canCreate, canEdit, canDelete, isAdmin } = usePermissionsContext();
  // v: permissões granulares do módulo financeiro repassadas para a aba Financeiro
  const canEditCharges = isAdmin || canEdit('financial_charges');
  const canDeleteCharges = isAdmin || canDelete('financial_charges');
  // v2.7.0: transferência de veículo exige permissão de edição do módulo
  const canTransferVehicle = isAdmin || canEdit('vehicles_list');
  const canViewFines = finesEnabled && (isAdmin || canView('vehicles_fines_view'));
  const canViewContracts = isAdmin || canView('crm_contracts');
  // v2.5.0: aba "Negócios" exige permissão de visualização do menu de negócios
  const canViewDeals = isAdmin || canView('crm_deals');
  // v2.5.0: aba "Tarefas" exige módulo habilitado no tenant + permissão de listagem
  const tasksTabEnabled = !!currentTenant?.tasks_enabled && (isAdmin || canView('tasks_list'));
  // Permissão para o botão "Novo Negócio" da sidebar (também depende de poder ver negócios)
  const canCreateDeal = canViewDeals && (isAdmin || canCreate('crm_deals'));
  // v2.6.0: aba "Financeiro" exige módulo financeiro no tenant + permissão de cobranças
  const financeiroTabEnabled =
    !!currentTenant?.financial_enabled && (isAdmin || canView('financial_charges'));



  // v2.6.0: contador da aba Financeiro (cobranças + assinaturas do veículo)
  const { data: vehicleFinanceiro } = useVehicleFinanceiro(
    vehicleId,
    open && financeiroTabEnabled,
  );
  const financeiroCount =
    (vehicleFinanceiro?.cobrancas?.length ?? 0) + (vehicleFinanceiro?.assinaturas?.length ?? 0);

  // v2.7.0: contador da aba Transferências (períodos de posse)
  const { total: transfersCount } = useVehicleOwnershipHistory(vehicleId, open);
  // Aba Vistoria depende do módulo Checklist (permissões validadas nas RPCs)
  const inspectionsEnabled = useChecklistModuleEnabled();
  // 2026-10-06: aba Equipamentos só com Estoque ligado e permissão de Movimentações
  const stockEnabled = useStockModuleEnabled();
  const equipmentEnabled = stockEnabled && (isAdmin || canView('stock_movements'));
  const { isEnabled: workflowEnabled } = useWorkflowModule();
  // 2026-10-09: contador da aba Equipamentos (instalados agora) — mesma consulta da aba
  const { data: equipmentData } = useVehicleEquipment(open && equipmentEnabled ? vehicleId : null);
  // 2026-10-09: aba O.S (módulo OS + permissão de listagem); total via KPI do servidor
  const serviceOrdersEnabled = useServiceOrderModuleEnabled() && (isAdmin || canView('os_list'));
  const { data: osCountData } = useServiceOrdersList({
    search: '', status: null, serviceTypeId: null, providerId: null, categoryId: null, phaseId: null,
    deadlineBucket: null, dateField: 'created_at', dateFrom: null, dateTo: null,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 1, vehicleId,
  }, open && !!vehicleId && serviceOrdersEnabled);

  const { vehicle, counts, isLoading, refetch } = useVehicleDetails(
    vehicleId,
    open,
  );

  // v2.10.0: status/dados do veículo e processos do Workflow atualizam sem F5
  useVehicleRecordRealtime({ vehicleId, enabled: open });
  useWorkflowRecordRealtime({ rootEntityId: vehicleId, enabled: open && workflowEnabled });

  /** Conteúdo interno do modal */
  const renderContent = () => {
    if (isLoading || !vehicle) {
      return (
        <div className="flex flex-1 flex-col gap-3 p-6">
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-full w-full" />
        </div>
      );
    }

    return (
      <TooltipProvider>

        <VehicleModalHeader
          vehicle={vehicle}
          onEdit={() => (onEdit ? onEdit(vehicle.id) : setEditOpen(true))}
          onOpenFines={() => setFinesOpen(true)}
          canViewFines={canViewFines}
          onTransfer={() => setTransferOpen(true)}
          canTransfer={canTransferVehicle}
          onClose={() => onOpenChange(false)}
        />

        <VehicleModalTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          dealsCount={counts?.deals}
          contractsCount={counts?.contracts}
          tasksCount={counts?.tasks}
          chatCount={counts?.conversations}
          finesCount={counts?.fines}
          notesCount={counts?.notes}
          attachmentsCount={attachmentsCount}
          hasContact={!!vehicle.contact_id}
          finesEnabled={canViewFines}
          canViewContracts={canViewContracts}
          // v2.5.0: permissões granulares das abas Negócios e Tarefas
          canViewDeals={canViewDeals}
          tasksEnabled={tasksTabEnabled}
          financeiroEnabled={financeiroTabEnabled}
          financeiroCount={financeiroCount}
          transfersCount={transfersCount}
          inspectionsCount={counts?.inspections}
          equipmentCount={equipmentData?.installed?.length}
          serviceOrdersCount={osCountData?.kpis?.total}
          serviceOrdersEnabled={serviceOrdersEnabled}
          inspectionsEnabled={inspectionsEnabled}
          workflowEnabled={workflowEnabled}
          equipmentEnabled={equipmentEnabled}
        />


        {/*
          Área de conteúdo: Conteúdo + SIDEBAR À DIREITA (padrão do modal de cliente)
          Cada aba controla seu próprio scroll interno quando necessário.
        */}
        <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-thin p-2 md:p-4">
            {activeTab === 'summary' && (
              <VehicleSummaryTab
                vehicle={vehicle}
                counts={counts}
                isActive
                /* v2.8.0: no mobile a ficha lateral é exibida dentro do Resumo */
                sidebarContent={
                  isMobile ? (
                    <VehicleModalSidebar
                      vehicle={vehicle}
                      canCreateDeal={canCreateDeal}
                      canEditPhoto={canTransferVehicle}
                      onDealCreated={() => refetch()}
                    />
                  ) : undefined
                }
              />
            )}

            {/* v2.1.0: abas reaproveitam 100% os componentes premium do modal de cliente */}
            {activeTab === 'deals' && (
              <CustomerDealsTab
                customerId={null}
                contactId={null}
                vehicleId={vehicle.id}
                vehicleLabel={[vehicle.plate, [vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                newDealContactId={vehicle.contact_id}
                isActive
                onOpenDeal={handleOpenDeal}
              />
            )}

            {activeTab === 'contracts' && (
              <CustomerContractsTab
                customerId={null}
                contactId={null}
                vehicleId={vehicle.id}
                isActive
              />
            )}

            {activeTab === 'tasks' && (
              /* v2.3.0: repassa contato e rótulo do veículo para pré-preencher "Nova Tarefa" */
              <TasksRelatedTabs
                context={{ vehicleId: vehicle.id, contactId: vehicle.contact_id }}
                vehicleLabel={
                  [vehicle.plate, [vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(' — ') || undefined
                }
              />
            )}

            {/* v2.6.0: cobranças/assinaturas vinculadas ao veículo */}
            {activeTab === 'financeiro' && (
              <VehicleFinanceiroTab
                vehicleId={vehicle.id}
                isActive
                canEdit={canEditCharges}
                canDelete={canDeleteCharges}
              />
            )}

            {activeTab === 'attachments' && (
              <VehicleAttachmentsTab
                vehicleId={vehicle.id}
                isActive
                onCountChange={setAttachmentsCount}
                onNavigateToTimeline={() => setActiveTab('summary')}
              />
            )}

            {/* v2.7.0: proprietários que já passaram pelo veículo */}
            {activeTab === 'transfers' && (
              <VehicleTransfersTab
                vehicleId={vehicle.id}
                isActive
                canTransfer={canTransferVehicle}
                onTransfer={() => setTransferOpen(true)}
              />
            )}

            {/* v2.2.0: histórico de auditoria (mesma estrutura do modal de tarefas) */}
            {activeTab === 'inspections' && <VehicleInspectionsTab vehicleId={vehicle.id} />}
            {activeTab === 'equipment' && equipmentEnabled && <VehicleEquipmentTab vehicleId={vehicle.id} />}
            {/* Área de altura total (igual ao Negócio): paginação fica no rodapé do modal */}
            {activeTab === 'service_orders' && serviceOrdersEnabled && (
              <div className="flex h-full min-h-0 flex-col">
                <ServiceOrdersEmbeddedList vehicleId={vehicle.id} />
              </div>
            )}

            {/* v2.9.0: processos do Workflow do veículo */}
            {activeTab === 'workflow' && workflowEnabled && (
              <WorkflowProcessPanel rootEntityType="vehicle" rootEntityId={vehicle.id} groupByStatus />
            )}

            {activeTab === 'history' && (
              <VehicleHistoryTab vehicleId={vehicle.id} isActive />
            )}


            {activeTab === 'chat' && (
              <CustomerChatTab
                contactId={vehicle.contact_id}
                contactName={vehicle.contact_name}
                isActive
                onOpenConversation={handleOpenConversation}
              />
            )}


            {activeTab === 'fines' && (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                <ReceiptText className="h-8 w-8 text-muted-foreground/60" />
                <div>
                  <p className="text-sm font-medium">
                    {formatCurrency(vehicle.fines_total_open)} em multas em aberto
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {vehicle.fines_synced_at
                      ? 'Consulte o detalhamento e sincronize novamente quando necessário.'
                      : 'Ainda não houve sincronização de multas para este veículo.'}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setFinesOpen(true)}>
                  Ver multas
                </Button>
              </div>
            )}
          </div>

          {/* v2.8.0: sidebar apenas no desktop (no mobile vai para a aba Resumo) */}
          {!isMobile && (
            <VehicleModalSidebar
              vehicle={vehicle}
              canCreateDeal={canCreateDeal}
              canEditPhoto={canTransferVehicle}
              onDealCreated={() => refetch()}
            />
          )}
        </div>


        {/* v2.7.0: transferência de propriedade (reaproveita transfer_vehicle_ownership) */}
        <VehicleTransferDialog
          open={transferOpen}
          onOpenChange={setTransferOpen}
          vehicleId={vehicle.id}
          plate={vehicle.plate}
          subtitle={[vehicle.brand_name, vehicle.model_name, vehicle.year]
            .filter(Boolean)
            .join(' · ')}
          currentOwnerName={vehicle.customer_name || vehicle.contact_name}
          onTransferred={() => refetch()}
        />

        {/* v2.9.0: edição do veículo direto do modal 360º quando não há onEdit externo */}
        {!onEdit && (
          <VehicleFormDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            scope={{
              customerId: vehicle.customer_id,
              contactId: vehicle.contact_id,
            }}
            allowOwnerSelection
            vehicle={vehicle as unknown as CustomerVehicle}
            onSaved={() => refetch()}
          />
        )}

        <VehicleFinesDialog
          open={finesOpen}
          onOpenChange={setFinesOpen}
          vehicleId={vehicle.id}
          plate={vehicle.plate}
          subtitle={[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · ')}
          canSync={canViewFines}
          onSynced={() => refetch()}
        />

        {/* v2.4.0: modal do negócio por cima do modal do veículo; ao fechar
            atualiza os contadores/lista caso o negócio tenha sido alterado */}
        <DealDetailsModalPremium
          dealId={selectedDealId}
          open={isDealModalOpen}
          onOpenChange={(o) => {
            setIsDealModalOpen(o);
            if (!o) {
              setSelectedDealId(null);
              refetch();
            }
          }}
        />

        {/* v2.6.0: conversa aberta a partir da aba "Chat" (mesmo padrão do modal de cliente) */}
        <ChatConversationModal
          conversationId={selectedConversationId}
          open={isChatModalOpen}
          onOpenChange={(o) => {
            setIsChatModalOpen(o);
            if (!o) setSelectedConversationId(null);
          }}
        />

      </TooltipProvider>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 flex flex-col overflow-hidden',
          isMobile
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0'
            : 'max-w-[95vw] w-[95vw] h-[97vh]',
        )}
        preventCloseOnOutsideInteraction
        hideCloseButton
      >
        <VisuallyHidden.Root>
          <DialogTitle>Detalhes do Veículo</DialogTitle>
        </VisuallyHidden.Root>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
